import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useTenant } from '../../contexts/TenantContext'

const PageEditor = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { tenant } = useTenant()
  
  const [pageData, setPageData] = useState({
    title: '',
    content: '',
    slug: '',
    template: 'default',
    meta_description: '',
    meta_keywords: '',
    status: 'draft',
    is_featured: false,
    featured_image: null,
    author: '',
    excerpt: '',
    publish_date: new Date().toISOString().split('T')[0]
  })
  
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})
  const [activeTab, setActiveTab] = useState('content')
  const [previewMode, setPreviewMode] = useState(false)
  const [savedMessage, setSavedMessage] = useState('')

  useEffect(() => {
    const template = searchParams.get('template')
    if (template) {
      setPageData(prev => ({
        ...prev,
        template: template,
        title: getTemplateDefaults(template).title,
        content: getTemplateDefaults(template).content
      }))
    }
    
    if (id) {
      fetchPage(id)
    }
  }, [id, searchParams])

  const getTemplateDefaults = (template) => {
    const templates = {
      homepage: {
        title: `Welcome to ${tenant?.name || 'Our Organization'}`,
        content: `<h1>Welcome to ${tenant?.name || 'Our Organization'}</h1>
<p>We are dedicated to promoting baseball excellence in our region.</p>
<h2>About Us</h2>
<p>Our organization has been serving the baseball community since [year]. We provide training, organize tournaments, and develop players at all levels.</p>
<h2>Contact Us</h2>
<p>Email: ${tenant?.contact_email || 'contact@organization.com'}<br>
Phone: ${tenant?.contact_phone || 'Phone number'}</p>`
      },
      about: {
        title: `About ${tenant?.name || 'Our Organization'}`,
        content: `<h1>About ${tenant?.name || 'Our Organization'}</h1>
<p>Write about your organization's mission, vision, and values here.</p>
<h2>Our Mission</h2>
<p>[Your mission statement]</p>
<h2>Our History</h2>
<p>[Your organization's history]</p>`
      },
      contact: {
        title: 'Contact Us',
        content: `<h1>Contact ${tenant?.name || 'Us'}</h1>
<p>Get in touch with us for any inquiries or support.</p>
<h2>Contact Information</h2>
<p><strong>Email:</strong> ${tenant?.contact_email || 'contact@organization.com'}<br>
<strong>Phone:</strong> ${tenant?.contact_phone || 'Phone number'}</p>`
      },
      history: {
        title: `History of ${tenant?.name || 'Our Organization'}`,
        content: `<h1>History of ${tenant?.name || 'Our Organization'}</h1>
<p>Discover the rich heritage and achievements of our organization.</p>
<h2>Foundation</h2>
<p>[Founding information]</p>
<h2>Milestones</h2>
<p>[Key achievements and milestones]</p>`
      }
    }
    return templates[template] || { title: '', content: '' }
  }

  const fetchPage = async (pageId) => {
    try {
      setLoading(true)
      const token = localStorage.getItem('tenant_admin_token')
      
      const response = await fetch(`/api/client-admin/pages/${pageId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'X-Original-Host': window.location.hostname
        }
      })

      if (response.ok) {
        const data = await response.json()
        setPageData(data.data || {})
      }
    } catch (error) {
      console.error('Failed to fetch page:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (isDraft = false, shouldNavigate = true) => {
    setSaving(true)
    setErrors({})

    try {
      const token = localStorage.getItem('tenant_admin_token')
      const saveData = {
        ...pageData,
        status: isDraft ? 'draft' : pageData.status
      }

      const url = id ? `/api/client-admin/pages/${id}` : '/api/client-admin/pages'
      const method = id ? 'PUT' : 'POST'
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Original-Host': window.location.hostname
        },
        body: JSON.stringify(saveData)
      })

      const data = await response.json()

      if (response.ok) {
        setSavedMessage(isDraft ? 'Draft saved successfully!' : 'Page saved successfully!')
        setTimeout(() => setSavedMessage(''), 3000)
        
        if (shouldNavigate) {
          setTimeout(() => navigate('/admin/cms'), 1000)
        }
      } else {
        if (data.errors) {
          setErrors(data.errors)
        } else {
          setErrors({
            general: data.message || 'Failed to save page'
          })
        }
      }
    } catch (error) {
      console.error('Failed to save page:', error)
      setErrors({
        general: 'Network error. Please try again.'
      })
    } finally {
      setSaving(false)
    }
  }

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-')
  }

  const handleTitleChange = (title) => {
    setPageData(prev => ({
      ...prev,
      title,
      slug: prev.slug || generateSlug(title)
    }))
  }

  const handleFileUpload = (field, file) => {
    setPageData(prev => ({
      ...prev,
      [field]: file
    }))
  }

  const tabs = [
    { id: 'content', label: 'Content', icon: 'fas fa-edit' },
    { id: 'settings', label: 'Settings', icon: 'fas fa-cog' },
    { id: 'seo', label: 'SEO', icon: 'fas fa-search' },
    { id: 'advanced', label: 'Advanced', icon: 'fas fa-code' }
  ]

  if (loading) {
    return (
      <div className="page-editor-loading">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <span>Loading page...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="page-editor">
      <div className="editor-header">
        <div className="header-content">
          <div className="breadcrumb">
            <span onClick={() => navigate('/admin/cms')} className="breadcrumb-link">
              <i className="fas fa-arrow-left"></i>
              CMS
            </span>
            <i className="fas fa-chevron-right"></i>
            <span>{id ? 'Edit Page' : 'Create New Page'}</span>
          </div>
          <h1>{id ? 'Edit Page' : 'Create New Page'}</h1>
          <p>Create and customize pages for your {tenant?.name || 'organization'} website</p>
        </div>
        
        <div className="header-actions">
          {savedMessage && (
            <div className="save-message">
              <i className="fas fa-check-circle"></i>
              {savedMessage}
            </div>
          )}
          
          <div className="action-buttons">
            <button 
              className="btn-secondary"
              onClick={() => setPreviewMode(!previewMode)}
            >
              <i className={`fas fa-${previewMode ? 'edit' : 'eye'}`}></i>
              {previewMode ? 'Edit Mode' : 'Preview'}
            </button>
            
            <button 
              className="btn-outline"
              onClick={() => handleSave(true, false)}
              disabled={saving}
            >
              <i className="fas fa-save"></i>
              Save Draft
            </button>
            
            <button 
              className="btn-primary"
              onClick={() => handleSave(false)}
              disabled={saving}
            >
              {saving ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-cloud-upload-alt"></i>}
              Publish
            </button>
          </div>
        </div>
      </div>

      <div className="editor-container">
        <div className="editor-sidebar">
          <div className="tab-navigation">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={tab.icon}></i>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="editor-content">
          {activeTab === 'content' && (
            <div className="content-tab">
              <div className="content-header">
                <h2>Page Content</h2>
                <p>Create and edit your page content</p>
              </div>

              <div className="content-form">
                <div className="form-group">
                  <label htmlFor="title">
                    <i className="fas fa-heading"></i>
                    Page Title
                  </label>
                  <input
                    type="text"
                    id="title"
                    value={pageData.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder="Enter page title"
                    className={errors.title ? 'error' : ''}
                  />
                  {errors.title && <span className="error-message">{errors.title}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="slug">
                    <i className="fas fa-link"></i>
                    URL Slug
                  </label>
                  <div className="slug-input">
                    <span className="slug-prefix">/{tenant?.slug || 'site'}/</span>
                    <input
                      type="text"
                      id="slug"
                      value={pageData.slug}
                      onChange={(e) => setPageData({ ...pageData, slug: e.target.value })}
                      placeholder="page-url"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="excerpt">
                    <i className="fas fa-quote-left"></i>
                    Excerpt
                  </label>
                  <textarea
                    id="excerpt"
                    value={pageData.excerpt}
                    onChange={(e) => setPageData({ ...pageData, excerpt: e.target.value })}
                    placeholder="Brief description of the page content"
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="content">
                    <i className="fas fa-edit"></i>
                    Page Content
                  </label>
                  <div className="content-editor">
                    <div className="editor-toolbar">
                      <button type="button" className="toolbar-btn" title="Bold">
                        <i className="fas fa-bold"></i>
                      </button>
                      <button type="button" className="toolbar-btn" title="Italic">
                        <i className="fas fa-italic"></i>
                      </button>
                      <button type="button" className="toolbar-btn" title="Underline">
                        <i className="fas fa-underline"></i>
                      </button>
                      <div className="toolbar-separator"></div>
                      <button type="button" className="toolbar-btn" title="Heading 1">
                        H1
                      </button>
                      <button type="button" className="toolbar-btn" title="Heading 2">
                        H2
                      </button>
                      <button type="button" className="toolbar-btn" title="Paragraph">
                        P
                      </button>
                      <div className="toolbar-separator"></div>
                      <button type="button" className="toolbar-btn" title="Link">
                        <i className="fas fa-link"></i>
                      </button>
                      <button type="button" className="toolbar-btn" title="Image">
                        <i className="fas fa-image"></i>
                      </button>
                      <button type="button" className="toolbar-btn" title="List">
                        <i className="fas fa-list"></i>
                      </button>
                    </div>
                    <textarea
                      id="content"
                      value={pageData.content}
                      onChange={(e) => setPageData({ ...pageData, content: e.target.value })}
                      placeholder="Write your page content here..."
                      rows="15"
                      className="content-textarea"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="featured_image">
                    <i className="fas fa-image"></i>
                    Featured Image
                  </label>
                  <div className="file-upload">
                    <input
                      type="file"
                      id="featured_image"
                      accept="image/*"
                      onChange={(e) => handleFileUpload('featured_image', e.target.files[0])}
                    />
                    <div className="upload-preview">
                      <i className="fas fa-cloud-upload-alt"></i>
                      <span>Click to upload featured image</span>
                      <small>Recommended size: 1200x630px</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="settings-tab">
              <div className="settings-header">
                <h2>Page Settings</h2>
                <p>Configure page visibility and behavior</p>
              </div>

              <div className="settings-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="status">
                      <i className="fas fa-toggle-on"></i>
                      Status
                    </label>
                    <select
                      id="status"
                      value={pageData.status}
                      onChange={(e) => setPageData({ ...pageData, status: e.target.value })}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="template">
                      <i className="fas fa-layout"></i>
                      Template
                    </label>
                    <select
                      id="template"
                      value={pageData.template}
                      onChange={(e) => setPageData({ ...pageData, template: e.target.value })}
                    >
                      <option value="default">Default</option>
                      <option value="homepage">Homepage</option>
                      <option value="about">About Page</option>
                      <option value="contact">Contact Page</option>
                      <option value="history">History Page</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="author">
                      <i className="fas fa-user"></i>
                      Author
                    </label>
                    <input
                      type="text"
                      id="author"
                      value={pageData.author}
                      onChange={(e) => setPageData({ ...pageData, author: e.target.value })}
                      placeholder="Page author name"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="publish_date">
                      <i className="fas fa-calendar"></i>
                      Publish Date
                    </label>
                    <input
                      type="date"
                      id="publish_date"
                      value={pageData.publish_date}
                      onChange={(e) => setPageData({ ...pageData, publish_date: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={pageData.is_featured}
                        onChange={(e) => setPageData({ ...pageData, is_featured: e.target.checked })}
                      />
                      <span className="checkmark"></span>
                      <div className="checkbox-content">
                        <strong>Featured Page</strong>
                        <p>Display this page prominently on the website</p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'seo' && (
            <div className="seo-tab">
              <div className="seo-header">
                <h2>SEO Settings</h2>
                <p>Optimize your page for search engines</p>
              </div>

              <div className="seo-form">
                <div className="form-group">
                  <label htmlFor="meta_description">
                    <i className="fas fa-search"></i>
                    Meta Description
                  </label>
                  <textarea
                    id="meta_description"
                    value={pageData.meta_description}
                    onChange={(e) => setPageData({ ...pageData, meta_description: e.target.value })}
                    placeholder="Brief description for search engines (150-160 characters)"
                    rows="3"
                    maxLength="160"
                  />
                  <div className="character-count">
                    {pageData.meta_description.length}/160 characters
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="meta_keywords">
                    <i className="fas fa-tags"></i>
                    Meta Keywords
                  </label>
                  <input
                    type="text"
                    id="meta_keywords"
                    value={pageData.meta_keywords}
                    onChange={(e) => setPageData({ ...pageData, meta_keywords: e.target.value })}
                    placeholder="keyword1, keyword2, keyword3"
                  />
                  <small>Separate keywords with commas</small>
                </div>

                <div className="seo-preview">
                  <h3>Search Preview</h3>
                  <div className="preview-card">
                    <div className="preview-title">{pageData.title || 'Page Title'}</div>
                    <div className="preview-url">
                      {window.location.origin}/{tenant?.slug || 'site'}/{pageData.slug || 'page-url'}
                    </div>
                    <div className="preview-description">
                      {pageData.meta_description || pageData.excerpt || 'Page description will appear here...'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'advanced' && (
            <div className="advanced-tab">
              <div className="advanced-header">
                <h2>Advanced Settings</h2>
                <p>Advanced configuration options</p>
              </div>

              <div className="advanced-form">
                <div className="form-group">
                  <label htmlFor="custom_css">
                    <i className="fas fa-code"></i>
                    Custom CSS
                  </label>
                  <textarea
                    id="custom_css"
                    value={pageData.custom_css || ''}
                    onChange={(e) => setPageData({ ...pageData, custom_css: e.target.value })}
                    placeholder="/* Add custom CSS for this page */"
                    rows="10"
                    className="code-editor"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="custom_js">
                    <i className="fas fa-code"></i>
                    Custom JavaScript
                  </label>
                  <textarea
                    id="custom_js"
                    value={pageData.custom_js || ''}
                    onChange={(e) => setPageData({ ...pageData, custom_js: e.target.value })}
                    placeholder="// Add custom JavaScript for this page"
                    rows="8"
                    className="code-editor"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {errors.general && (
        <div className="error-notification">
          <i className="fas fa-exclamation-triangle"></i>
          {errors.general}
        </div>
      )}
    </div>
  )
}

export default PageEditor